import React from 'react';
import { Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { CountStatsSection } from 'modules/home/components/CountStatsSection/CountStatsSection';
import { calculateActivitiesCountDelta } from 'modules/home/utils';
import { DeltaIcon } from 'modules/home/components/CountStatsSection/DeltaIcon';

interface IndividualsSectionProps {
  isLoading: boolean
  totalIndividuals: number
  individualsByAgeGroup: { minAge: number, maxAge?: number | null, count: number }[]
  previousIndividualsByAgeGroup?: { minAge: number, maxAge?: number | null, count: number }[]
  renderEmptyState: () => React.ReactNode
  formatAgeGroupLabel: (minAge: number, maxAge?: number | null) => string
}

export const IndividualsSection: React.FC<IndividualsSectionProps> = ({
  isLoading,
  totalIndividuals,
  individualsByAgeGroup,
  previousIndividualsByAgeGroup,
  renderEmptyState,
  formatAgeGroupLabel
}) => {
  const { formatMessage } = useIntl();
  const getPreviousAgeGroupCount = (minAge: number) =>
    previousIndividualsByAgeGroup?.find(group => group.minAge === minAge)?.count;

  const renderDeltaCell = (current: number, previous?: number) => {
    const delta = previous != undefined ? calculateActivitiesCountDelta(current, previous) : undefined;
    if (delta == undefined) {
      return <Typography variant='body2'>-</Typography>;
    }

    return (
      <Stack direction='row' justifyContent='flex-end' alignItems='center' gap={0.5}>
        <Typography variant='body2'>{`${delta}%`}</Typography>
        <DeltaIcon currentValue={current} previousValue={previous ?? 0} />
      </Stack>
    );
  };

  return (
    <CountStatsSection
      title={formatMessage({ id: 'Individuals' })}
      totalCount={totalIndividuals}
      isLoading={isLoading}
    >
      <Paper sx={{ p: 2, flex: '1 1 100%' }}>
        <Typography variant='h6' gutterBottom>{formatMessage({ id: 'Individuals by age group' })}</Typography>
        {individualsByAgeGroup.length
          ? (
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>{formatMessage({ id: 'Age group' })}</TableCell>
                  <TableCell align='right'>{formatMessage({ id: 'Individuals' })}</TableCell>
                  <TableCell align='right'>{formatMessage({ id: 'Delta' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {individualsByAgeGroup.map(group => (
                  <TableRow key={group.minAge}>
                    <TableCell>{formatAgeGroupLabel(group.minAge, group.maxAge)}</TableCell>
                    <TableCell align='right'>{group.count}</TableCell>
                    <TableCell align='right'>{renderDeltaCell(group.count, getPreviousAgeGroupCount(group.minAge))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
          : renderEmptyState()
        }
      </Paper>
    </CountStatsSection>
  );
};
